import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Camera, ScanBarcode, List, Clock, BarChart3, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import HttpService from '@/shared/services/http.service';
import { API_CONFIG } from '@/shared/constants/api';

// Scales font size down as the count grows more digits, so 4-5 digit numbers
// still fit inside the same fixed-size circle as a 1-2 digit count would.
const countTextSizeClass = (count: number | null): string => {
  const digits = count === null ? 1 : String(count).length;
  if (digits <= 2) return 'text-2xl';
  if (digits === 3) return 'text-xl';
  if (digits === 4) return 'text-base';
  return 'text-sm';
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const [totalPackages, setTotalPackages] = useState<number | null>(null);
  const [overduePackages, setOverduePackages] = useState<number | null>(null);

  const fetchStorageSummary = async () => {
    try {
      const response = await HttpService.get(API_CONFIG.path.storageSummary, {}, { skipNotify: true });
      setTotalPackages(response?.currentlyInStorage ?? null);
      setOverduePackages(response?.olderThan7Days ?? null);
    } catch {
      setTotalPackages(null);
      setOverduePackages(null);
    }
  };

  useEffect(() => {
    fetchStorageSummary();
  }, []);

  return (
    <div className="p-6">
      <div className="mb-6">
        <p className="text-muted-foreground">{t('select_an_action_to')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        {/* Analytics Dashboard */}
        {user?.role === 'admin' && (
          <motion.div
            whileTap={{ scale: 0.97 }}
            className="action-card cursor-pointer"
            onClick={() => navigate('/analytics')}
          >
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                <BarChart3 className="w-8 h-8 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-bold text-foreground">{t('dashboard')}</h2>
                <p className="text-muted-foreground text-sm mt-1">{t('view_analytics')}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground shrink-0" />
            </div>
          </motion.div>
        )}

        {/* All Packages Section */}
        <motion.div
          whileTap={{ scale: 0.97 }}
          className="action-card cursor-pointer"
          onClick={() => navigate('/slot-list')}
        >
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-warning/10 flex items-center justify-center shrink-0 px-1">
              {totalPackages === null ? (
                <List className="w-8 h-8 text-warning" />
              ) : (
                <span
                  className={`font-extrabold text-warning leading-none tabular-nums ${countTextSizeClass(totalPackages)}`}
                >
                  {totalPackages}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-foreground">{t('all_packages_1')}</h2>
              <p className="text-muted-foreground text-sm mt-1">{t('view_all_stored_packages')}</p>
            </div>
            <ArrowRight className="w-5 h-5 text-muted-foreground shrink-0" />
          </div>
        </motion.div>

        <motion.div
          whileTap={{ scale: 0.97 }}
          className="action-card cursor-pointer"
          onClick={() => navigate('/scan?mode=inbound')}
        >
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
              <Camera className="w-8 h-8 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-foreground">{t('receive_parcel')}</h2>
              <p className="text-muted-foreground text-sm mt-1">{t('scan_parcel_to_assign')}</p>
            </div>
            <ArrowRight className="w-5 h-5 text-muted-foreground shrink-0" />
          </div>
        </motion.div>

        <motion.div
          whileTap={{ scale: 0.97 }}
          className="action-card cursor-pointer"
          onClick={() => navigate('/scan?mode=outbound')}
        >
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-success/10 flex items-center justify-center shrink-0">
              <ScanBarcode className="w-8 h-8 text-success" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-foreground">{t('find_parcel')}</h2>
              <p className="text-muted-foreground text-sm mt-1">{t('quickly_scan_to_locate')}</p>
            </div>
            <ArrowRight className="w-5 h-5 text-muted-foreground shrink-0" />
          </div>
        </motion.div>

        {/* Picked Packages Section */}
        {user?.role === 'admin' && (
          <motion.div
            whileTap={{ scale: 0.97 }}
            className="action-card cursor-pointer"
            onClick={() => navigate('/picked-packages')}
          >
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-bold text-foreground">{t('picked_packages')}</h2>
                <p className="text-muted-foreground text-sm mt-1">{t('view_picked_packages_desc')}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground shrink-0" />
            </div>
          </motion.div>
        )}

        {/* Overdue Packages Section */}
        <motion.div
          whileTap={{ scale: 0.97 }}
          className="action-card cursor-pointer"
          onClick={() => navigate('/overdue-packages')}
        >
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center shrink-0 px-1">
              {overduePackages === null ? (
                <Clock className="w-8 h-8 text-red-500" />
              ) : (
                <span
                  className={`font-extrabold text-red-500 leading-none tabular-nums ${countTextSizeClass(overduePackages)}`}
                >
                  {overduePackages}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-foreground">{t('overdue_packages')}</h2>
              <p className="text-muted-foreground text-sm mt-1">{t('view_overdue_packages')}</p>
            </div>
            <ArrowRight className="w-5 h-5 text-muted-foreground shrink-0" />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
